import React, { useEffect, useState, useMemo } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip } from "react-leaflet";
import { api } from "@/lib/api";
import { Filter, Layers } from "lucide-react";

const TYPES = {
  solar:        { label: "Solar",         color: "#F59E0B" },
  street_light: { label: "Street Light",  color: "#EAB308" },
  biogas:       { label: "Biogas",        color: "#10B981" },
  cbg:          { label: "CBG",           color: "#059669" },
  waste_energy: { label: "Waste-to-Energy", color: "#06B6D4" },
  hydro:        { label: "Hydro",         color: "#3B82F6" },
  ev:           { label: "EV",            color: "#6366F1" },
};

export default function GISDashboardPage() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(Object.keys(TYPES));
  useEffect(() => { api.get("/projects").then((r) => setItems(r.data.items)); }, []);
  const filtered = useMemo(()=>items.filter(p=>active.includes(p.project_type)), [items, active]);

  const counts = useMemo(()=>{
    const c = {}; items.forEach(p => c[p.project_type] = (c[p.project_type]||0)+1);
    return c;
  }, [items]);

  const toggle = (k) => setActive((a) => a.includes(k) ? a.filter(x=>x!==k) : [...a, k]);

  return (
    <div className="container-x py-8">
      <div className="label-tiny text-[hsl(var(--primary))]">Geo-Spatial Intelligence</div>
      <h1 className="mt-1 text-3xl lg:text-4xl font-heading font-bold">Punjab GIS Dashboard</h1>
      <p className="mt-2 max-w-2xl text-muted-foreground">Interactive map of all PEDA projects across Punjab. Filter by type.</p>

      <div className="mt-6 grid lg:grid-cols-4 gap-4">
        <div className="lg:col-span-3 rounded-2xl border border-border bg-card overflow-hidden" style={{ height: 560 }} data-testid="gis-map-container">
          <MapContainer center={[30.9, 75.85]} zoom={7} scrollWheelZoom={true} style={{ height: "100%", width: "100%" }}>
            <TileLayer
              attribution='&copy; OpenStreetMap contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {filtered.map((p) => (
              <CircleMarker
                key={p.id}
                center={[p.latitude, p.longitude]}
                radius={6 + Math.min(8, (p.progress || 0) / 15)}
                pathOptions={{ color: TYPES[p.project_type]?.color || "#00A859", fillColor: TYPES[p.project_type]?.color || "#00A859", fillOpacity: 0.7 }}
              >
                <Tooltip>{p.name}</Tooltip>
                <Popup>
                  <div className="text-xs">
                    <div className="font-semibold">{p.name}</div>
                    <div className="opacity-70">{p.district} · {p.project_type}</div>
                    <div className="mt-1">Status: <span className="font-medium">{p.status}</span></div>
                    <div>Progress: <span className="font-medium">{p.progress}%</span></div>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
        <div className="space-y-4">
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="flex items-center gap-2 mb-3"><Filter className="h-4 w-4"/><div className="label-tiny">Layers</div></div>
            <div className="space-y-2">
              {Object.entries(TYPES).map(([k, v]) => (
                <label key={k} className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={active.includes(k)} onChange={()=>toggle(k)}/>
                  <span className="h-3 w-3 rounded-full" style={{ background: v.color }}/>
                  <span className="text-sm flex-1">{v.label}</span>
                  <span className="text-xs text-muted-foreground">{counts[k] || 0}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="label-tiny">Snapshot</div>
            <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
              <Stat k="Sites" v={items.length}/>
              <Stat k="Filtered" v={filtered.length}/>
              <Stat k="Districts" v={new Set(items.map(i=>i.district)).size}/>
              <Stat k="Types" v={Object.keys(TYPES).length}/>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
function Stat({ k, v }) {
  return (
    <div className="rounded-md bg-muted p-3">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</div>
      <div className="font-heading font-bold text-xl">{v}</div>
    </div>
  );
}
